import { EntitiesStore } from './entities-store.abstract';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap, mergeMap, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { EventGeneralInfoItem } from 'world-archery-lib';
import { WaHelperService } from '../../../library/wa-services/wa-helper-service';
import { CountryDependencyEntityStore } from './country-dependency-entity-store';
import { Injector } from '@angular/core';
import { CategoryCombo } from '../filters-store/filters-store';
import { catchError } from 'rxjs/operators/';
import { of, Observable, forkJoin } from 'rxjs';
import { DivisionsStore, IEvents, CompetitionStore } from './competition-store';

/**
 * Interface for competition options of tofficlal service.
 *
 * @export
 * @interface ICompOption
 */
export interface ICompOption {
  url: string;
  label: string;
  type: string;
}

/**
 * Interface for division on competition documents of tofficial service.
 *
 * @export
 * @interface ICompetitionDocumentDivision
 */
export interface ICompetitionDocumentDivision {
  id: number;
  label: string;
}

/**
 * Interface for competition document from tofficial service.
 *
 * @export
 * @interface ICompetitionDocument
 */
export interface ICompetitionLink {
  label: string;
  url: string;
}

/**
 * Interface for the result of api response from tofficial service.
 *
 * @export
 * @interface ICompetitionDocumentResult
 */
export interface ICompetitionDocumentResult {
  compOptions: ICompOption[];
  disciplineId: number;
  disciplineLabel: string;
  divisions: ICompetitionDocumentDivision[];
  documents: ICompetitionLink[];
  links: ICompetitionLink[];
}

const mockData =
{
  "compOptions": [
    {
      "url": null,
      "label": "CUSTOMREG",
      "type": "CUSTOMREG"
    }
  ],
  "disciplineId": 2,
  "disciplineLabel": "Indoor Archery",
  "divisions": [
    {
      "id": 2,
      "label": "Compound"
    },
    {
      "id": 1,
      "label": "Recurve"
    }
  ],
  "documents": [
    {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    },
    {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    }, {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    },
    {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    }, {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    },
    {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    }, {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    },
    {
      "label": "RESULTS_WAC1995",
      "url": "https://documents.worldarchery.sport/Events/World_Championships/Target/Results_Archive/RESULTS_WAC1995.pdf"
    },
  ],
  "links": [
    {
      "label": "Results",
      "url": "https://worldarchery.org/competition/23126/",
      "type": "web"
    }
  ]
};


/**
 * Competition documents store from the tofficial service.
 *
 * @export
 * @class CompetitionDocumentStore
 * @extends {EntitiesStore<ICompetitionDocumentResult>}
 */
export class CompetitionDocumentStore extends EntitiesStore<ICompetitionDocumentResult> {

  /**
   * The competition document endpoint.
   */
  documents_endpoint: string;

  /**
   * The competition id.
   *
   * @type {string}
   * @memberof CompetitionStore
   */
  public competition_id: string;

  /**
   * The depencency injector.
   */
  injectorS: Injector;

  constructor(http: HttpClient, injector: Injector) {
    super(undefined, http, []);
    this.injectorS = injector;
    this.documents_endpoint = environment.api_url + "/v3/DOC-FILE/"
  }

  /** EntitiesStore "requestData" method overwriting to handle dependencies independently.
   * Each dependency failure won't affect others - they'll just return empty data.
   *
   * @memberof EntitiesStore
   */
  requestData(params: any, request_more: boolean, isLive = false, time = 0): Observable<any> {
    this.loadingRef = true;
    this.requestMore = request_more;

    // Create stores for each dependency
    const divisionsStore = new DivisionsStore(this.http, this.injectorS);
    const docFilesStore = new DocFilesStore(this.http, this.injectorS);
    const disciplineStore = new DisciplineStore(this.http, this.injectorS);
    const competitionStore = new CompetitionStore(this.http, this.injectorS);

    // Store them as dependencies for getRequest to access
    this.dependencies = [
      { entity: divisionsStore, params: { 'RBP': 'ALL' } },
      { entity: docFilesStore, params: { 'RBP': 'ALL', 'CompId': this.competition_id } },
      { entity: disciplineStore, params: { 'RBP': 'ALL' } },
      { entity: competitionStore, params: { 'RBP': 'ALL', 'CompId': this.competition_id } },
    ];

    // Request each dependency with error handling to prevent cascade failures
    const divisionRequest$ = divisionsStore.requestData({ 'RBP': 'ALL' }, false)
      .pipe(catchError(err => {
        console.warn('Failed to load divisions:', err);
        return of([]);
      }));

    const docFilesRequest$ = docFilesStore.requestData({ 'RBP': 'ALL', 'CompId': this.competition_id }, false)
      .pipe(catchError(err => {
        console.warn('Failed to load documents:', err);
        return of([]);
      }));

    const disciplineRequest$ = disciplineStore.requestData({ 'RBP': 'ALL' }, false)
      .pipe(catchError(err => {
        console.warn('Failed to load disciplines:', err);
        return of([]);
      }));

    const competitionRequest$ = competitionStore.requestData({ 'RBP': 'ALL', 'CompId': this.competition_id }, false)
      .pipe(catchError(err => {
        console.warn('Failed to load competition data:', err);
        return of([]);
      }));

    // Use forkJoin but with individual error handling so one failure doesn't break all
    return forkJoin([
      divisionRequest$,
      docFilesRequest$,
      disciplineRequest$,
      competitionRequest$
    ]).pipe(
      mergeMap(() => {
        // After all requests complete (successfully or with handled errors), process the data
        return this.getRequest(params, request_more);
      })
    );
  }

  /**
   * {@inheritdoc}
   *
   * @memberof CompetitionStore.
   */
  getRequest(params: any, request_more: boolean) {

    /**
     * Bunch of variables to trim data calls.
     */
    const divisionData = this.dependencies[0].entity.data || [];
    const documentData = this.dependencies[1].entity.data || [];
    const disciplinesData = this.dependencies[2].entity.data || [];
    const competitionData: IEvents[] = this.dependencies[3].entity.data || [];

    /**
     * DivisionsStore result reordered to adapt to ICompetitionDocumentDivision.
     */
    const divisions: ICompetitionDocumentDivision[] = [];
    try {
      for (let i = 0; i < divisionData.length; i++) {
        if (divisionData[i] && divisionData[i].Id && divisionData[i].Name) {
          divisions.push({
            id: divisionData[i].Id,
            label: divisionData[i].Name
          })
        }
      }
    } catch (error) {
      console.warn('Error processing divisions data:', error);
    }

    /**
     * DocFilesStore result reordered to adapt to ICompetitionLink.
     */
    const documents: ICompetitionLink[] = [];
    try {
      for (let i = 0; i < documentData.length; i++) {
        if (documentData[i] && documentData[i].Name && documentData[i].URL) {
          documents.push({
            label: documentData[i].Name,
            url: documentData[i].URL
          })
        }
      }
    } catch (error) {
      console.warn('Error processing documents data:', error);
    }

    /**
     * DisciplineStore result sorted by CompetitionStore ComDis param
     * to get the discipline id and label.
     */
    let disciplineId: number = null;
    let disciplineLabel: string = '';
    try {
      if (competitionData.length > 0 && competitionData[0].ComDis) {
        const competitionDisciplineId = competitionData[0].ComDis;
        const discipline = disciplinesData.find(d => d && d.Id === competitionDisciplineId);
        if (discipline) {
          disciplineId = discipline.Id;
          disciplineLabel = discipline.Discipline || '';
        } else {
          console.warn(`Discipline with ID ${competitionDisciplineId} not found`);
        }
      }
    } catch (error) {
      console.warn('Error processing discipline data:', error);
    }

    /**
     * Final object format according to ICompetitionDocumentResult interface.
     */
    const competitionDocumentResult: ICompetitionDocumentResult = {
      compOptions: [],
      disciplineId: disciplineId,
      disciplineLabel: disciplineLabel,
      divisions: divisions,
      documents: documents,
      links: []
    }

    /**
     * Final object as observable and returned to the getRequest() caller.
     * Since dependencies are now handled independently in requestData,
     * we can simplify error handling here.
     */
    const competitionDocumentResultAsObservable = of(competitionDocumentResult);
    return competitionDocumentResultAsObservable
      .pipe(
        map(a => {
          this.data = [a as any];
          this.dataStream.next(this.data);
        }),
        catchError(e => {
          console.error('Unexpected error in competition documents getRequest:', e);
          // This should rarely happen now since dependencies handle their own errors
          return of([]);
        })
      ) as any;
  }

  /**
   * Method for check if the competition has documents.
   *
   * @returns
   * @memberof CompetitionStore
   */
  hasDocuments() {
    return this.data && this.data[0] && this.data[0].documents.length > 0;
  }

  /**
   * Method for check if the competition has links.
   *
   * @returns
   * @memberof CompetitionStore
   */
  hasLinks() {
    return this.data && this.data[0] && this.data[0].links.length > 0;
  }
};

// Class dependency to fetch documents from competition.
export class DocFilesStore extends EntitiesStore<ICompetitionLink> {
  constructor(http: HttpClient, injector: Injector) {
    super('DOC-FILE', http);
  }
}

// Class dependency to fetch all disciplines.
export class DisciplineStore extends EntitiesStore<any> {
  constructor(http: HttpClient, injector: Injector) {
    super('COMPETITIONDISCIPLINES', http);
  }
}
